import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCommitImportMutation } from '../../../../../api/schedulingApi';
import type { ImportCommitResult } from '../../../../../types/scheduling';

interface CommitStepProps {
  importId: string;
  studioId: string;
  scheduleId: string;
  scheduleName: string;
  onBack: () => void;
  onCommitted: (result: ImportCommitResult) => void;
  onError: (message: string) => void;
}

/**
 * Step 3 — choose add vs replace and commit. Replace is destructive so it
 * requires typing the schedule name to confirm (matches the backend guard).
 */
const CommitStep: React.FC<CommitStepProps> = ({
  importId,
  studioId,
  scheduleId,
  scheduleName,
  onBack,
  onCommitted,
  onError,
}) => {
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const [confirm, setConfirm] = useState('');
  const [commit, { isLoading }] = useCommitImportMutation();

  const replaceReady = mode === 'add' || confirm.trim() === scheduleName;

  let commitLabel = 'Add to schedule';
  if (isLoading) commitLabel = 'Committing…';
  else if (mode === 'replace') commitLabel = 'Replace schedule';

  const handleCommit = async () => {
    try {
      const result = await commit({
        id: importId,
        studioId,
        scheduleId,
        mode,
        confirm: mode === 'replace' ? confirm.trim() : undefined,
      }).unwrap();
      onCommitted(result);
    } catch (err: any) {
      onError(err?.data?.detail || 'Commit failed. Please try again.');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose how to apply the imported sessions to{' '}
        <strong>{scheduleName}</strong>. Imported classes are pinned (marked
        manually scheduled) so a later algorithm run will not move them.
      </Typography>

      <FormControl>
        <RadioGroup
          value={mode}
          onChange={e => setMode(e.target.value as 'add' | 'replace')}
        >
          <FormControlLabel
            value="add"
            control={<Radio />}
            label="Add to the schedule (keep what's already there; duplicate sessions are skipped)"
          />
          <FormControlLabel
            value="replace"
            control={<Radio />}
            label="Replace the schedule's contents (removes current placements first)"
          />
        </RadioGroup>
      </FormControl>

      {mode === 'replace' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This removes all current placements on this schedule before adding
            the imported ones. It is fully undoable, but to continue, type the
            schedule name <strong>{scheduleName}</strong> below.
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder={scheduleName}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
          />
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleCommit}
          disabled={isLoading || !replaceReady}
        >
          {commitLabel}
        </Button>
      </Stack>
    </Box>
  );
};

export default CommitStep;
